import React, { useMemo } from 'react'
import { csx, Text } from '@vtex/admin-ui'
import { graphql } from 'babel-plugin-relay/macro'
import { useFragment } from 'react-relay-offline'
import { FormattedMessage } from 'react-intl'

import { ProductSkuAvailabilityRow } from './ProductSkuAvailabilityRow'
import {
  Badge,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerTitle,
  useDrawer,
} from '../../../../ui/components'
import { AvailabilityOptionDrawerItem } from './AvailabilityOptionDrawerItem'
import type { ProductSkuAvailabilityDelivery_ProductSkuOfferConnection$key } from './__generated__/ProductSkuAvailabilityDelivery_ProductSkuOfferConnection.graphql'
import {
  useFormatCurrency,
  useOrderFormShippingEstimate,
} from '../../../Checkout/hooks'
import { DeliveryAvailabilityPrice } from './DeliveryAvailabilityPrice'
import { getBestDeliveryAvailabilities } from './utils'
import { DeliveryChannelIcon, Price } from '../../../../core/components'

const productSkuAvailabilityConnectionFragment = graphql`
  fragment ProductSkuAvailabilityDelivery_ProductSkuOfferConnection on ProductSkuOfferConnection {
    edges {
      node {
        deliveryChannel
        netPrice {
          amount
        }
        deliveryAvailabilities {
          edges {
            node {
              name
              shippingEstimate
              netPrice {
                amount
              }
            }
          }
        }
      }
    }
  }
`

type DeliveryAvailabilities = Array<{
  skuOfferNetPrice: number
  node: {
    name: string
    shippingEstimate: string
    netPrice: {
      amount: number
    }
  }
}>

export type ProductSkuAvailabilityDeliveryProps = {
  busy?: boolean
  productSkuOfferConnectionRef: ProductSkuAvailabilityDelivery_ProductSkuOfferConnection$key
}

export const ProductSkuAvailabilityDelivery = ({
  busy,
  productSkuOfferConnectionRef,
}: ProductSkuAvailabilityDeliveryProps) => {
  const { getEstimateMessage } = useOrderFormShippingEstimate()
  const formatCurrency = useFormatCurrency()

  const deliveryOptionsDrawer = useDrawer({
    route: '#/availability-delivery-drawer',
  })

  const productSkuOffers = useFragment(
    productSkuAvailabilityConnectionFragment,
    productSkuOfferConnectionRef
  )

  const deliveryOffers = useMemo(() => {
    return productSkuOffers.edges.filter(
      (productSkuOffer) => productSkuOffer.node.deliveryChannel === 'DELIVERY'
    )
  }, [productSkuOffers.edges])

  const deliveryAvailabilities = useMemo(() => {
    const availabilities = deliveryOffers.reduce((acc, currentOffer) => {
      return [
        ...acc,
        ...currentOffer.node.deliveryAvailabilities.edges.map(
          (deliveryAvailability) => ({
            ...deliveryAvailability,
            skuOfferNetPrice: currentOffer.node.netPrice.amount / 100,
          })
        ),
      ]
    }, [] as DeliveryAvailabilities)

    const { cheapest, fastest } = getBestDeliveryAvailabilities(availabilities)

    return availabilities
      .map((availability) => ({
        ...availability,
        isCheapest: cheapest.node.name === availability.node.name,
        isFastest: fastest.node.name === availability.node.name,
      }))
      .sort((a, b) => {
        if (a.isCheapest && !b.isCheapest) return -1
        if (!a.isCheapest && b.isCheapest) return 1
        if (a.isFastest && !b.isFastest) return -1
        if (!a.isFastest && b.isFastest) return 1

        return 0
      })
  }, [deliveryOffers])

  const renderProductSkuAvailabilityMessage = () => {
    const cheapestDelivery = deliveryAvailabilities.find(
      ({ isCheapest }) => isCheapest
    )

    if (!cheapestDelivery) {
      return <FormattedMessage id="unavailable" />
    }

    const cheapestShippingEstimate = getEstimateMessage(
      cheapestDelivery.node.shippingEstimate
    )

    const cheapestPrice = formatCurrency(
      cheapestDelivery.node.netPrice.amount / 100
    )

    return (
      <FormattedMessage
        id="pdp.sku-availability.delivery.message"
        values={{
          shippingEstimate: cheapestShippingEstimate,
          shippingValue: cheapestPrice,
        }}
      />
    )
  }

  const handleSlaBadgeClick = () => {
    deliveryOptionsDrawer.open()
  }

  const handleDrawerClose = () => {
    deliveryOptionsDrawer.close()
  }

  const renderRightColumn = () => {
    if (!deliveryOffers.length) return null

    return (
      <Badge onClick={handleSlaBadgeClick}>
        <FormattedMessage
          id="pdp.sku-availability.options-quantity"
          values={{
            optionsQuantity: deliveryAvailabilities.length,
          }}
        />
      </Badge>
    )
  }

  return (
    <>
      <ProductSkuAvailabilityRow
        icon={<DeliveryChannelIcon deliveryChannel="DELIVERY" />}
        title={<FormattedMessage id="pdp.sku-availability.delivery.title" />}
        message={renderProductSkuAvailabilityMessage()}
        status={deliveryOffers.length > 0 ? 'available' : 'unavailable'}
        rightColumn={renderRightColumn()}
        busy={busy}
        data-testid="product-sku-availability-delivery"
      />
      <Drawer isOpen={deliveryOptionsDrawer.isOpen} onClose={handleDrawerClose}>
        <DrawerCloseButton />
        <DrawerTitle>
          <FormattedMessage id="pdp.sku-availability.drawer-delivery.title" />
        </DrawerTitle>
        <DrawerBody>
          {deliveryAvailabilities.map((deliveryAvailability) => (
            <AvailabilityOptionDrawerItem
              key={deliveryAvailability.node.name}
              data-testid="product-sku-availability-row-sla-option-drawer-item"
              icon={
                <>
                  <DeliveryChannelIcon
                    deliveryChannel="DELIVERY"
                    className={csx({
                      color: '$gray50',
                      marginRight: '$space-2',
                    })}
                  />
                  <Text variant="detail" tone="secondary">
                    <Price value={deliveryAvailability.skuOfferNetPrice} />
                  </Text>
                </>
              }
              title={
                <Text variant="action1">
                  {getEstimateMessage(
                    deliveryAvailability.node.shippingEstimate
                  )}
                </Text>
              }
              description={
                <Text variant="action2" tone="secondary">
                  {deliveryAvailability.node.name}
                </Text>
              }
              price={
                <DeliveryAvailabilityPrice
                  price={deliveryAvailability.node.netPrice.amount}
                />
              }
              info={
                <>
                  {deliveryAvailability.isCheapest && (
                    <Badge type="success">
                      <FormattedMessage id="checkout.shipping.sla-cheapest" />
                    </Badge>
                  )}
                  {deliveryAvailability.isFastest && (
                    <Badge type="success">
                      <FormattedMessage id="checkout.shipping.sla-fastest" />
                    </Badge>
                  )}
                </>
              }
            />
          ))}
        </DrawerBody>
      </Drawer>
    </>
  )
}
